import { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import {
    Headphones, MessageSquare, Clock, CheckCircle, AlertTriangle,
    Loader2, RefreshCw, Building2, User, Send
} from 'lucide-react';
import { toast } from 'sonner';

const statusConfig = {
    open: { label: 'Abierto', color: 'bg-red-500/10 text-red-400 border-red-500/30' },
    in_progress: { label: 'En Progreso', color: 'bg-amber-500/10 text-amber-400 border-amber-500/30' },
    waiting_client: { label: 'Esperando Cliente', color: 'bg-sky-500/10 text-sky-400 border-sky-500/30' },
    resolved: { label: 'Resuelto', color: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30' },
    closed: { label: 'Cerrado', color: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/30' },
};

const priorityConfig = {
    critical: { label: 'Critico', color: 'bg-red-500 text-white' },
    high: { label: 'Alto', color: 'bg-orange-500 text-white' },
    medium: { label: 'Medio', color: 'bg-amber-500/10 text-amber-400' },
    low: { label: 'Bajo', color: 'bg-zinc-500/10 text-zinc-400' },
};

const categoryLabels = {
    bug: 'Error / Bug', feature_request: 'Solicitud', billing: 'Facturacion',
    access: 'Acceso', performance: 'Rendimiento', other: 'Otro',
};

export default function AdminSupportPage() {
    const { apiCall, admin } = useAdminAuth();
    const [tickets, setTickets] = useState([]);
    const [stats, setStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [filterStatus, setFilterStatus] = useState('all');
    const [filterPriority, setFilterPriority] = useState('all');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [replyStatus, setReplyStatus] = useState('in_progress');
    const [sending, setSending] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            const params = new URLSearchParams();
            if (filterStatus !== 'all') params.append('status', filterStatus);
            if (filterPriority !== 'all') params.append('priority', filterPriority);
            const [tRes, sRes] = await Promise.all([
                apiCall('get', `/admin/support/tickets?${params.toString()}`),
                apiCall('get', '/admin/support/stats'),
            ]);
            setTickets(tRes.data);
            setStats(sRes.data);
        } catch { toast.error('Error al cargar tickets'); }
        finally { setLoading(false); }
    }, [apiCall, filterStatus, filterPriority]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const openTicket = async (ticket) => {
        try {
            const res = await apiCall('get', `/admin/support/tickets/${ticket.id}`);
            setSelectedTicket(res.data);
            setReplyStatus(res.data.status === 'open' ? 'in_progress' : res.data.status);
        } catch { toast.error('Error'); }
    };

    const sendReply = async () => {
        if (!replyText.trim()) { toast.error('Escribe una respuesta'); return; }
        setSending(true);
        try {
            await apiCall('post', `/admin/support/tickets/${selectedTicket.id}/reply`, {
                message: replyText, status: replyStatus,
            });
            toast.success('Respuesta enviada');
            setReplyText('');
            const res = await apiCall('get', `/admin/support/tickets/${selectedTicket.id}`);
            setSelectedTicket(res.data);
            fetchData();
        } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
        finally { setSending(false); }
    };

    const updateTicketStatus = async (ticketId, status) => {
        try {
            await apiCall('patch', `/admin/support/tickets/${ticketId}`, { status });
            toast.success(`Ticket ${statusConfig[status]?.label || status}`);
            if (selectedTicket?.id === ticketId) {
                setSelectedTicket({ ...selectedTicket, status });
            }
            fetchData();
        } catch { toast.error('Error'); }
    };

    if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;

    return (
        <div className="space-y-6" data-testid="admin-support-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="support-title">
                        Soporte Tecnico
                    </h1>
                    <p className="text-muted-foreground mt-1">Tickets reportados por las empresas</p>
                </div>
                <Button variant="outline" onClick={fetchData} data-testid="refresh-tickets">
                    <RefreshCw className="w-4 h-4 mr-2" />Actualizar
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                <Card className="card-aviation"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono" data-testid="stat-total">{stats.total || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Total</p>
                </CardContent></Card>
                <Card className="card-aviation border-red-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-red-400" data-testid="stat-open">{stats.open || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Abiertos</p>
                </CardContent></Card>
                <Card className="card-aviation border-amber-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-amber-400" data-testid="stat-progress">{stats.in_progress || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">En Progreso</p>
                </CardContent></Card>
                <Card className="card-aviation border-emerald-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-emerald-400" data-testid="stat-resolved">{stats.resolved || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Resueltos</p>
                </CardContent></Card>
                <Card className="card-aviation border-red-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-red-400" data-testid="stat-critical">{stats.critical || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Criticos</p>
                </CardContent></Card>
                <Card className="card-aviation border-orange-500/30"><CardContent className="p-3 text-center">
                    <p className="text-2xl font-bold font-mono text-orange-400" data-testid="stat-high">{stats.high || 0}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">Prioridad Alta</p>
                </CardContent></Card>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-[180px]" data-testid="filter-ticket-status"><SelectValue placeholder="Estado" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {Object.entries(statusConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={filterPriority} onValueChange={setFilterPriority}>
                    <SelectTrigger className="w-[180px]" data-testid="filter-ticket-priority"><SelectValue placeholder="Prioridad" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        {Object.entries(priorityConfig).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                    </SelectContent>
                </Select>
            </div>

            {/* Ticket List */}
            <div className="space-y-3">
                {tickets.length === 0 ? (
                    <Card className="card-aviation"><CardContent className="py-12 text-center text-muted-foreground">
                        <Headphones className="w-12 h-12 mx-auto mb-3 opacity-40" />
                        <p>No hay tickets de soporte</p>
                    </CardContent></Card>
                ) : tickets.map(t => {
                    const sc = statusConfig[t.status] || statusConfig.open;
                    const pc = priorityConfig[t.priority] || priorityConfig.medium;
                    return (
                        <Card key={t.id} className={`card-aviation cursor-pointer hover:border-amber-500/30 transition-colors ${t.status === 'open' ? 'border-l-4 border-l-red-500' : t.status === 'in_progress' ? 'border-l-4 border-l-amber-500' : ''}`}
                              onClick={() => openTicket(t)} data-testid={`ticket-${t.id}`}>
                            <CardContent className="p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap mb-1">
                                            <Badge className={pc.color} variant="outline">{pc.label}</Badge>
                                            <Badge className={sc.color} variant="outline">{sc.label}</Badge>
                                            <Badge variant="outline" className="text-[10px]">{categoryLabels[t.category] || t.category}</Badge>
                                            {t.module && <Badge variant="outline" className="text-[10px] font-mono">{t.module}</Badge>}
                                        </div>
                                        <p className="font-semibold text-sm">{t.subject}</p>
                                        <p className="text-xs text-muted-foreground mt-1 truncate">{t.description}</p>
                                    </div>
                                    <div className="text-right text-xs text-muted-foreground flex-shrink-0">
                                        <div className="flex items-center gap-1"><Building2 className="w-3 h-3" />{t.tenant_name}</div>
                                        <div className="flex items-center gap-1 mt-1"><User className="w-3 h-3" />{t.created_by_name}</div>
                                        <div className="flex items-center gap-1 mt-1"><Clock className="w-3 h-3" />{new Date(t.created_at).toLocaleDateString('es')}</div>
                                        <p className="font-mono mt-1">{(t.messages || []).length} msgs</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>

            {/* Ticket Detail Dialog */}
            <Dialog open={!!selectedTicket} onOpenChange={() => setSelectedTicket(null)}>
                <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="ticket-detail-dialog">
                    <DialogHeader>
                        <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase">
                            {selectedTicket?.subject}
                        </DialogTitle>
                    </DialogHeader>
                    {selectedTicket && (
                        <div className="space-y-4">
                            {/* Ticket info */}
                            <div className="flex flex-wrap gap-2 text-xs">
                                <Badge className={priorityConfig[selectedTicket.priority]?.color}>{priorityConfig[selectedTicket.priority]?.label}</Badge>
                                <Badge className={statusConfig[selectedTicket.status]?.color}>{statusConfig[selectedTicket.status]?.label}</Badge>
                                <Badge variant="outline">{categoryLabels[selectedTicket.category] || selectedTicket.category}</Badge>
                                <Badge variant="outline"><Building2 className="w-3 h-3 mr-1" />{selectedTicket.tenant_name}</Badge>
                                <Badge variant="outline"><User className="w-3 h-3 mr-1" />{selectedTicket.created_by_name}</Badge>
                            </div>

                            {/* Quick status actions */}
                            <div className="flex gap-2">
                                {selectedTicket.status !== 'resolved' && (
                                    <Button size="sm" variant="outline" className="text-emerald-500 border-emerald-500/30 h-7 text-xs" onClick={() => updateTicketStatus(selectedTicket.id, 'resolved')}>
                                        <CheckCircle className="w-3 h-3 mr-1" />Resolver
                                    </Button>
                                )}
                                {selectedTicket.status !== 'closed' && selectedTicket.status === 'resolved' && (
                                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => updateTicketStatus(selectedTicket.id, 'closed')}>Cerrar</Button>
                                )}
                            </div>

                            {/* Messages */}
                            <div className="space-y-3 max-h-[300px] overflow-y-auto border rounded-lg p-3 bg-muted/20">
                                {(selectedTicket.messages || []).map(msg => (
                                    <div key={msg.id} className={`flex ${msg.sender_type === 'staff' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`max-w-[80%] rounded-lg p-3 text-sm ${msg.sender_type === 'staff' ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-muted/50 border border-border/50'}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className="font-semibold text-xs">{msg.sender_name}</span>
                                                <Badge variant="outline" className="text-[9px] h-4">{msg.sender_type === 'staff' ? 'Soporte' : 'Cliente'}</Badge>
                                                <span className="text-[10px] text-muted-foreground">{new Date(msg.created_at).toLocaleString('es')}</span>
                                            </div>
                                            <p className="whitespace-pre-wrap">{msg.message}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Reply */}
                            {selectedTicket.status !== 'closed' && (
                                <div className="space-y-3 border-t pt-3">
                                    <Textarea value={replyText} onChange={e => setReplyText(e.target.value)}
                                              placeholder="Escribe tu respuesta..." rows={3} data-testid="ticket-reply-input" />
                                    <div className="flex items-center justify-between">
                                        <Select value={replyStatus} onValueChange={setReplyStatus}>
                                            <SelectTrigger className="w-[200px] h-8 text-xs" data-testid="reply-status-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="in_progress">En Progreso</SelectItem>
                                                <SelectItem value="waiting_client">Esperando Cliente</SelectItem>
                                                <SelectItem value="resolved">Resuelto</SelectItem>
                                            </SelectContent>
                                        </Select>
                                        <Button onClick={sendReply} disabled={sending} data-testid="send-reply-btn">
                                            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                                            Enviar Respuesta
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
